import React, { useEffect, useState } from 'react'
import { graphql } from 'gatsby'
import slugify from 'slugify'
import { InView } from 'react-intersection-observer'
import { Scrollspy } from '@makotot/ghostui'

import Image from '../components/Images/Image'
import PageWrap from '../components/Layouts/PageWrap'
import PillarHeader from '../components/Pillar/PillarHeader'
import PillarContent from '../components/Pillar/PillarContent'
import PillarStickyNav from '../components/Pillar/PillarStickyNav/PillarStickyNav'

const PillarPage = ({ data }) => {
  const head = data.page.acfPillarPage.head
  head.title = data.page.title

  const content = data.page.acfPillarPage.content

  const [currentChapter, setCurrentChapter] = useState(1)
  const [showStickyNav, setShowStickyNav] = useState(false)

  let chapters = content.filter(item => {
    return item.fieldGroupName === 'post_Acfpillarpage_Content_Chapter'
  })

  chapters = chapters.map((item, idx) => {
    item.index = idx + 1
    item.slug = slugify(item.text, {
      remove: /[^0-9a-z\s]/gi,
      lower: true,
    })
    item.href = '#' + item.slug + '-' + (idx + 1)
    return item
  })

  const chapterRefs = []
  const setChapterRef = chapterRef => {
    if (
      chapterRef.current?.dataset &&
      chapterRef.current.dataset.hasOwnProperty('index')
    ) {
      chapterRefs[parseInt(chapterRef.current.dataset.index, 10)] = chapterRef
    }
  }

  const chapterGrouped = []
  let whichChapter = 0
  for (let block of content) {
    // update chapter bucket
    if ('post_Acfpillarpage_Content_Chapter' === block.fieldGroupName) {
      whichChapter = block.index
    }
    chapterGrouped[whichChapter] = chapterGrouped[whichChapter] || []
    chapterGrouped[whichChapter].push(block)
  }

  return (
    <PageWrap seo={data?.page?.seo}>
      <article className="pillar-page -mt-20 md:-mt-40">
        {head.image?.srcSet && (
          <Image
            src={head.image.mediaItemUrl}
            srcSet={head.image.srcSet}
            width={head.image.mediaDetails.width}
            height={head.image.mediaDetails.height}
          />
        )}

        <InView
          as="div"
          onChange={(inView, entry) => {
            const el = document.querySelector('.pillar-header')
            if (
              Math.abs(entry.boundingClientRect.top) >
              entry.boundingClientRect.height
            ) {
              setShowStickyNav(true)
            } else {
              setShowStickyNav(false)
            }
          }}
        >
          <PillarHeader head={head} chapters={chapters} />
        </InView>

        {chapters && (
          <PillarStickyNav
            chapters={chapters}
            currentIndex={currentChapter}
            show={showStickyNav}
          />
        )}

        {content && (
          <Scrollspy sectionRefs={chapterRefs} offset={-300}>
            {({ currentElementIndexInViewport, elementsStatusInViewport }) => {
              if (
                0 < currentElementIndexInViewport &&
                currentChapter !== currentElementIndexInViewport
              ) {
                setCurrentChapter(currentElementIndexInViewport)
              }

              return (
                <PillarContent
                  content={content}
                  setChapterRef={setChapterRef}
                  currentChapter={currentChapter}
                />
              )
            }}
          </Scrollspy>
        )}
      </article>
    </PageWrap>
  )
}

export default PillarPage

export const query = graphql`
  query($id: String!) {
    page: wpPost(id: { eq: $id }) {
      seo {
        ...seoPostTypeFields
      }
      title
      acfPillarPage {
        content {
          ... on WpPost_Acfpillarpage_Content_Chapter {
            fieldGroupName
            label
            tag
            text
          }
          ... on WpPost_Acfpillarpage_Content_Image {
            fieldGroupName
            layout
            image {
              id
              srcSet
              mediaItemUrl
              mediaDetails {
                height
                width
                sizes {
                  height
                  sourceUrl
                  width
                }
              }
            }
            includeCaption
            caption
          }
          ... on WpPost_Acfpillarpage_Content_Related {
            fieldGroupName
            label
            link {
              target
              title
              url
            }
          }
          ... on WpPost_Acfpillarpage_Content_Heading {
            fieldGroupName
            tag
            text
          }
          ... on WpPost_Acfpillarpage_Content_Wysiwyg {
            content
            fieldGroupName
          }
          ... on WpPost_Acfpillarpage_Content_Cta {
            content
            fieldGroupName
            button {
              url
              title
              target
            }
          }
          ... on WpPost_Acfpillarpage_Content_Contributors {
            fieldGroupName
            label
            siteUsers
          }
        }

        head {
          fieldGroupName
          crumbs {
            link {
              target
              title
              url
            }
          }
          image {
            id
            altText
            mediaItemUrl
            mediaDetails {
              sizes {
                width
                sourceUrl
                name
                mimeType
                height
                fileSize
                file
              }
              height
              width
              file
            }
            srcSet
          }
          toc {
            label
          }
          pdf {
            label
            link {
              target
              title
              url
            }
            text
          }
        }
      }
    }
  }
`
